import React, { useEffect, useState } from 'react'
import ProtectedRoute from '../../routes/protected'
import SidebarCargosSalarios from '../../components/sidebars/sidebarCargosSalarios'
import salaryAnalysisService from '../../services/data/salary-analysis.service'
import companyService from '../../services/data/company.service'
import currencyFormat from '../../helpers/currencyFormat'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Dropdown } from 'primereact/dropdown'

const METRICS = [
    { key: 'total_expected_positions', label: 'Posições previstas', numeric: true },
    { key: 'total_headcount', label: 'Colaboradores', numeric: true },
    { key: 'total_open_positions', label: 'Vagas abertas', numeric: true },
    { key: 'total_salary_mass', label: 'Massa salarial', numeric: true, money: true },
    { key: 'avg_salary', label: 'Salário médio', numeric: true, money: true },
    { key: 'approved_budget', label: 'Orçamento aprovado', numeric: true, money: true },
    { key: 'budget_used_pct', label: '% Orçamento utilizado', numeric: true, suffix: '%' },
    { key: 'count_within_range', label: 'Dentro da faixa', numeric: true },
    { key: 'count_above_range', label: 'Acima da faixa', numeric: true },
    { key: 'count_below_range', label: 'Abaixo da faixa', numeric: true },
    { key: 'count_no_structure', label: 'Sem estrutura', numeric: true },
    { key: 'count_no_data', label: 'Sem dados', numeric: true },
]

function formatValue(metric, value) {
    if (value === null || value === undefined) return '-'
    if (metric.money) return currencyFormat(value)
    if (metric.suffix) return `${value}${metric.suffix}`
    return value
}

function deltaPct(base, compare) {
    if (base == null || compare == null || base === 0) return null
    return ((compare - base) / base * 100).toFixed(1)
}

export default function CompararDepartamentos() {
    const [departments, setDepartments] = useState([null, null])
    const [departmentItems, setDepartmentItems] = useState([])
    const [result, setResult] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        companyService.autoCompleteDepartment('')
            .then(r => setDepartmentItems(r || []))
            .catch(() => setDepartmentItems([]))
    }, [])

    function setDepartmentSel(idx, val) {
        const updated = [...departments]
        updated[idx] = val
        setDepartments(updated)
    }

    function addSlot() {
        if (departments.length >= 3) return
        setDepartments([...departments, null])
    }

    function compare() {
        const ids = departments.filter(Boolean).map(d => d.id)
        if (ids.length < 2) {
            setError('Selecione ao menos 2 departamentos para comparar.')
            return
        }
        setError('')
        setLoading(true)
        salaryAnalysisService.compararDepartamentos(ids)
            .then(setResult)
            .catch(e => setError(JSON.stringify(e?.response?.data || e)))
            .finally(() => setLoading(false))
    }

    return (
        <ProtectedRoute>
            <div className="container-fluid">
                <div className='row flex-nowrap'>
                    <SidebarCargosSalarios />
                    <div className="col-xs-10 col-sm-9 col-md-9 col-xl-10 p-0 formMarginBottom">
                        <div className='col text-bg-green p-3'>
                            <span className="fs-6 fw-bold">Comparativo de Departamentos</span>
                        </div>

                        <div className='row m-3 g-3 align-items-end'>
                            {departments.map((sel, idx) => (
                                <div key={idx} className='col-md-3'>
                                    <label className='form-label small fw-bold'>Departamento {idx + 1}</label>
                                    <Dropdown className='w-100' placeholder={`Departamento ${idx + 1}`}
                                        value={sel}
                                        options={departmentItems}
                                        optionLabel='title'
                                        filter
                                        showClear
                                        onChange={e => setDepartmentSel(idx, e.value)} />
                                </div>
                            ))}
                            {departments.length < 3 && (
                                <div className='col-auto'>
                                    <button className='btn btn-outline-success btn-sm' onClick={addSlot}>
                                        <FontAwesomeIcon icon={solid('plus')} /> Add departamento
                                    </button>
                                </div>
                            )}
                            <div className='col-auto'>
                                <button className='btn btn-success btn-sm' onClick={compare}>
                                    <FontAwesomeIcon icon={solid('scale-balanced')} /> Comparar
                                </button>
                            </div>
                        </div>

                        {error && <div className='alert alert-danger mx-3'>{error}</div>}

                        {loading && (
                            <div className='text-center p-5'>
                                <div className="spinner-border text-success" role="status" />
                            </div>
                        )}

                        {!loading && result.length >= 2 && (
                            <div className='m-3 table-responsive'>
                                <table className='table table-bordered table-sm'>
                                    <thead className='table-light'>
                                        <tr>
                                            <th>Indicador</th>
                                            {result.map((d, i) => (
                                                <th key={i} className='text-center'>
                                                    {d.department_title}
                                                    {d.department_code && (
                                                        <div className='text-muted small fw-normal'>{d.department_code}</div>
                                                    )}
                                                </th>
                                            ))}
                                            {result.length === 2 && <th className='text-center'>Δ%</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {METRICS.map(metric => {
                                            const values = result.map(r => r[metric.key])
                                            const delta = result.length === 2 && metric.numeric
                                                ? deltaPct(values[0], values[1])
                                                : null
                                            return (
                                                <tr key={metric.key}>
                                                    <td className='fw-bold'>{metric.label}</td>
                                                    {values.map((val, i) => (
                                                        <td key={i} className='text-end'>
                                                            {formatValue(metric, val)}
                                                        </td>
                                                    ))}
                                                    {result.length === 2 && (
                                                        <td className={`text-center fw-bold ${delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-muted'}`}>
                                                            {delta !== null ? `${delta > 0 ? '+' : ''}${delta}%` : '-'}
                                                        </td>
                                                    )}
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ProtectedRoute>
    )
}
